import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  MapPin,
  User as UserIcon,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Layers,
  Utensils,
  Target,
  Lock,
  Calendar,
  Phone,
  Mail,
  Briefcase,
  Building2,
  FileText,
  Clock,
  Heart
} from "lucide-react";
import { useProvinces, useDistricts, useWards } from "@/hooks/useAddress";
import { useMySignedNdas } from "@/hooks/useNda";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useFranchiseRequest } from "@/hooks/useFranchise";
import { useOfferFranchiseRequest, useMyFranchiseRequestOffers } from "@/hooks/useOffer";
import { useAuthStore } from "@/store/authStore";
import { useToggleSaveListing } from "@/hooks/useSavedListings";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner, NdaSignModal, OfferModal } from "@/components/common";
import { cn } from "@/lib/utils";
import type { FranchiseRequestImage } from "@/types";
import { formatPriceExact } from "@/utils";
import { NumberInput } from "@/components/ui/numberInput";
import { Textarea } from "@/components/ui/misc";

function getImages(images?: any[]) {
  if (!images?.length) return [];
  return images.map((i) => ({
    url: i.image?.publicUrl || i.url || i.publicUrl || "",
    type: i.type,
    isPrimary: i.isPrimary,
  })).filter(img => Boolean(img.url));
}

const offerSchema = z.object({
  message: z.string().min(10, "Tối thiểu 10 ký tự"),
  proposedRent: z.preprocess(
    (v) => (v === '' || v === undefined ? undefined : Number(String(v).replace(/\D/g, ''))),
    z.number().positive('Nhập mức giá đề xuất').optional()
  ),
});
type OfferData = z.infer<typeof offerSchema>;

export default function FranchiseRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const { data: rawData, isLoading, error, refetch } = useFranchiseRequest(id!);
  const item = (rawData as any)?.data ?? rawData;
  const isNdaError = error && (error as any).response?.data?.message === "Please Sign NDA";

  const { mutate: createOffer, isPending } = useOfferFranchiseRequest();
  const { isSaved, toggleSave } = useToggleSaveListing();
  const saved = item ? isSaved(item.id) : false;
  const { data: myOffers } = useMyFranchiseRequestOffers(user?.id);
  const isOffered = Array.isArray(myOffers) ? myOffers.some((o: any) => o.entityId === item?.id) : false;

  const { data: mySignedNdas } = useMySignedNdas();
  const isSignedNda = Array.isArray(mySignedNdas) ? mySignedNdas.some((n: any) => n.entityId === item?.id && n.entityType === 'FranchiseRequest') : false;
  const needsToSignNda = false;

  const { data: provinces } = useProvinces(item?.countryId || 1);
  const { data: districts } = useDistricts(item?.provinceId);
  const { data: wards } = useWards(item?.districtId);

  const provinceName = item?.province?.name || provinces?.find(p => p.id === item?.provinceId)?.name;
  const districtName = item?.district?.name || districts?.find(d => d.id === item?.districtId)?.name;
  const wardName = item?.ward?.name || wards?.find(w => w.id === item?.wardId)?.name;
  const countryName = item?.country?.name || (item?.countryId === 1 ? 'Việt Nam' : '');

  const [imgIdx, setImgIdx] = useState(0);
  const [offerOpen, setOfferOpen] = useState(false);
  const [ndaOpen, setNdaOpen] = useState(false);
  const [sent, setSent] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<OfferData>({
    resolver: zodResolver(offerSchema),
  });

  if (isLoading) return <Spinner />;

  if (isNdaError) {
    return (
      <div className="max-w-3xl mx-auto py-20 text-center animate-fade-in">
        <div className="bg-white rounded-2xl shadow-sm border p-10 max-w-lg mx-auto">
          <div className="w-16 h-16 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock size={32} />
          </div>
          <h2 className="text-2xl font-bold mb-3 text-gray-800">Yêu cầu bảo mật thông tin</h2>
          <p className="text-gray-500 mb-8 leading-relaxed">
            Tin đăng này yêu cầu bạn phải ký Thỏa thuận bảo mật thông tin (NDA) trước khi xem chi tiết và trao đổi với người đăng.
          </p>
          <Button
            onClick={() => setNdaOpen(true)}
            className="w-full btn-brand py-3 text-base mb-3"
          >
            Ký NDA để xem chi tiết
          </Button>
          <Link to="/nhu-cau-nhuong-quyen">
            <Button variant="ghost" className="w-full">
              Quay lại danh sách
            </Button>
          </Link>
        </div>

        <NdaSignModal
          isOpen={ndaOpen}
          onClose={() => setNdaOpen(false)}
          entityType="FranchiseRequest"
          entityId={id!}
          description="Bạn cần ký Thỏa thuận bảo mật thông tin (NDA) để xem chi tiết và trao đổi với người đăng."
          onSuccess={() => {
            setNdaOpen(false);
            refetch();
          }}
        />
      </div>
    );
  }

  if (!item)
    return (
      <div className="text-center py-20">
        <p style={{ color: "var(--text-3)" }}>Không tìm thấy nhu cầu này</p>
        <Link to="/nhu-cau-nhuong-quyen">
          <Button variant="ghost" className="mt-4">
            Quay lại danh sách
          </Button>
        </Link>
      </div>
    );

  const imgs = getImages(item.images);
  const title = item.name || "Yêu cầu hợp tác nhượng quyền";

  const fullAddr = [
    item.preferredLocation,
    wardName,
    districtName,
    provinceName,
    countryName,
  ]
    .filter(Boolean)
    .join(", ");

  const onSubmitOffer = (data: OfferData) => {
    const payload = {
      entityType: "FranchiseRequest",
      entityId: item.id,
      senderId: user?.id,
      receiverId: item.creator?.id || item.createdBy || "",
      content: {
        title: title,
        proposedRent: data.proposedRent,
        message: data.message,
      },
      message: data.message,
      status: "pending",
    };
    createOffer(payload as any, {
      onSuccess: () => {
        setSent(true);
        reset();
        setOfferOpen(false);
      },
    });
  };

  const isOwner = user?.id === item.creator?.id || user?.id === item.createdBy;

  const sectorName = item.businessSectorName || item.businessSector;
  const typeName = item.businessTypeName || item.businessType;

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-fade-in pb-20">
      {/* Header breadcrumb */}
      <div className="flex items-center justify-between gap-4">
        <div
          className="flex items-center gap-2 text-sm"
          style={{ color: "var(--text-3)" }}
        >
          <Link
            to="/nhu-cau-nhuong-quyen"
            className="flex items-center gap-1 hover:text-orange-500 transition-colors"
          >
            <ArrowLeft size={14} /> Nhu cầu nhượng quyền
          </Link>
          <span>/</span>
          <span className="truncate max-w-xs" style={{ color: "var(--text-1)" }}>
            {title}
          </span>
        </div>

        {isOwner && (
          <Link to={`/nhu-cau-nhuong-quyen/sua/${item.id}`}>
            <Button variant="outline" size="sm" className="gap-2 font-bold shadow-sm h-9 border-orange-200 text-orange-600 hover:bg-orange-50 hover:text-orange-700">
              Chỉnh sửa tin đăng
            </Button>
          </Link>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* ── Left: Main content ── */}
        <div className="lg:col-span-2 space-y-5">
          {/* Image gallery */}
          <Card className="overflow-hidden">
            <div className="relative h-64 md:h-80 bg-gray-100">
              {imgs.length > 0 ? (
                <>
                  <img
                    src={imgs[imgIdx].url}
                    alt={title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = "";
                    }}
                  />
                  {imgs.length > 1 && (
                    <>
                      <button
                        onClick={() =>
                          setImgIdx((i) => (i - 1 + imgs.length) % imgs.length)
                        }
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-white transition-colors"
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <button
                        onClick={() => setImgIdx((i) => (i + 1) % imgs.length)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-white transition-colors"
                      >
                        <ChevronRight size={16} />
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {imgs.map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setImgIdx(i)}
                            className={cn(
                              "h-1.5 rounded-full transition-all",
                              i === imgIdx
                                ? "w-5 bg-white"
                                : "w-1.5 bg-white/50",
                            )}
                          />
                        ))}
                      </div>
                    </>
                  )}
                  <div className="absolute top-3 left-3">
                    <span
                      className={cn(
                        "fnb-tag text-[11px] font-semibold",
                        item.approvalStatus === "approved"
                          ? "fnb-tag-green"
                          : "fnb-tag-brand",
                      )}
                    >
                      {item.approvalStatus === "approved"
                        ? "✓ Đã duyệt"
                        : "● Chờ duyệt"}
                    </span>
                  </div>
                </>
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center text-8xl opacity-20 select-none"
                  style={{ background: "var(--brand-pale)" }}
                >
                  🤝
                </div>
              )}
            </div>
            {/* Thumbnail strip */}
            {imgs.length > 1 && (
              <div
                className="flex gap-2 p-3 overflow-x-auto border-t"
                style={{ borderColor: "var(--border-c)" }}
              >
                {imgs.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setImgIdx(i)}
                    className={cn(
                      "shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all",
                      i === imgIdx
                        ? "border-orange-500"
                        : "border-transparent opacity-60 hover:opacity-100",
                    )}
                  >
                    <img
                      src={img.url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </Card>

          {/* Title + Overview */}
          <Card>
            <CardContent className="pt-6">
              <h1
                className="font-display font-bold text-2xl mb-4"
                style={{ color: "var(--text-1)" }}
              >
                {title}
              </h1>

              {/* Budget block */}
              <div
                className="rounded-xl p-4 mb-4 flex flex-wrap gap-6"
                style={{
                  background: "var(--brand-pale)",
                  border: "1px solid rgba(249,115,22,0.2)",
                }}
              >
                <div>
                  <p
                    className="text-xs font-medium mb-1"
                    style={{ color: "var(--text-3)" }}
                  >
                    Ngân sách đầu tư dự kiến
                  </p>
                  <p
                    className="font-display font-black text-xl"
                    style={{ color: "var(--brand)" }}
                  >
                    {item.budgetMin || item.budgetMax
                      ? `${item.budgetMin ? formatPriceExact(String(item.budgetMin)) : '0'} - ${item.budgetMax ? formatPriceExact(String(item.budgetMax)) : 'Thỏa thuận'} ${item.currencyUnit || 'VND'}`
                      : 'Thỏa thuận'}
                  </p>
                </div>
              </div>

              {fullAddr && (
                <div
                  className="flex items-center gap-1.5 text-sm"
                  style={{ color: "var(--text-3)" }}
                >
                  <MapPin size={14} className="text-orange-500 shrink-0" />
                  <span>Vị trí / Địa điểm mong muốn: <strong>{fullAddr}</strong></span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Key Parameters */}
          <Card>
            <CardHeader>
              <CardTitle>Thông tin yêu cầu nhượng quyền</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-2">
                {[
                  {
                    icon: <Building2 size={15} />,
                    label: "Ngành kinh doanh",
                    value: sectorName || "Tất cả ngành",
                  },
                  {
                    icon: <Utensils size={15} />,
                    label: "Loại hình kinh doanh",
                    value: typeName || "Tất cả mô hình",
                  },
                  {
                    icon: <Layers size={15} />,
                    label: "Diện tích mong muốn",
                    value: item.preferredAreaSize ? `${item.preferredAreaSize} ${item.areaUnit || 'm²'}` : "Không yêu cầu",
                  },
                  {
                    icon: <Calendar size={15} />,
                    label: "Thời gian dự kiến bắt đầu",
                    value: item.expectedStartDate ? new Date(item.expectedStartDate).toLocaleDateString("vi-VN") : "Càng sớm càng tốt",
                  },
                ].map(({ icon, label, value }) => (
                  <div key={label} className="flex items-start gap-2.5">
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                      style={{
                        background: "var(--brand-dim)",
                        color: "var(--brand)",
                      }}
                    >
                      {icon}
                    </div>
                    <div>
                      <p
                        className="text-[11px] font-medium"
                        style={{ color: "var(--text-3)" }}
                      >
                        {label}
                      </p>
                      <p
                        className="text-sm font-semibold"
                        style={{ color: "var(--text-1)" }}
                      >
                        {value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Description & Experience */}
          <Card>
            <CardHeader>
              <CardTitle>Mô tả & Kinh nghiệm</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {item.description && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: "var(--text-3)" }}>
                    <FileText size={14} className="text-orange-500" /> Mô tả chi tiết nhu cầu
                  </p>
                  <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              )}

              {item.businessExperience && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: "var(--text-3)" }}>
                    <Briefcase size={14} className="text-orange-500" /> Kinh nghiệm kinh doanh & vận hành
                  </p>
                  <div className="p-4 rounded-xl border border-orange-200/50" style={{ background: "var(--brand-pale)" }}>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {item.businessExperience}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ── Right: Sidebar ── */}
        <div className="space-y-4">
          {/* Contact Card */}
          <Card>
            <CardHeader>
              <CardTitle>Người đăng nhu cầu</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: "var(--brand-dim)" }}
                >
                  <UserIcon size={18} style={{ color: "var(--brand)" }} />
                </div>
                <div>
                  <p
                    className="font-semibold text-sm"
                    style={{ color: "var(--text-1)" }}
                  >
                    {item.creator?.username || "Bên nhận nhượng quyền"}
                  </p>
                  <p className="text-xs" style={{ color: "var(--text-3)" }}>
                    {item.creator?.email || item.contactEmail || ""}
                  </p>
                </div>
              </div>

              {(item.contactPhone || item.contactEmail) && (
                <div className="space-y-2 text-xs border-t pt-3" style={{ borderColor: "var(--border-c)" }}>
                  {item.contactPhone && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 flex items-center gap-1">
                        <Phone size={12} /> Số điện thoại:
                      </span>
                      <a href={`tel:${item.contactPhone}`} className="font-semibold text-orange-600 hover:underline">
                        {item.contactPhone}
                      </a>
                    </div>
                  )}
                  {item.contactEmail && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 flex items-center gap-1">
                        <Mail size={12} /> Email:
                      </span>
                      <span className="font-medium text-gray-800 truncate max-w-[150px]">{item.contactEmail}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Success Alert */}
              {sent && !offerOpen && (
                <div className="p-3 rounded-xl border flex items-center gap-2 text-sm"
                  style={{ background: '#f0fdf4', borderColor: '#bbf7d0', color: '#15803d' }}>
                  Đã gửi thông tin hợp tác thành công!
                </div>
              )}

              {!isOwner && (
                user ? (
                  <>
                    {needsToSignNda ? (
                      <button
                        onClick={() => setNdaOpen(true)}
                        className="w-full py-2.5 text-sm rounded-xl font-semibold flex items-center justify-center gap-1.5 transition-all btn-brand"
                      >
                        Ký NDA để Gửi thông tin
                      </button>
                    ) : (
                      <button
                        onClick={() => setOfferOpen(true)}
                        disabled={isOffered || sent}
                        className={cn(
                          "w-full py-2.5 text-sm rounded-xl font-semibold flex items-center justify-center gap-1.5 transition-all",
                          (isOffered || sent)
                            ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                            : "btn-brand"
                        )}
                      >
                        {isOffered || sent ? "Đã gửi thông tin" : "Gửi đề xuất hợp tác"}
                      </button>
                    )}
                  </>
                ) : (
                  <Link
                    to="/login"
                    className="btn-brand w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold"
                  >
                    Đăng nhập để gửi thông tin
                  </Link>
                )
              )}

              {!isOwner && (
                <button
                  onClick={() => toggleSave(item.id, "FranchiseRequest")}
                  className={cn(
                    "w-full py-2.5 text-sm rounded-xl font-semibold flex items-center justify-center gap-1.5 border transition-all mt-2",
                    saved
                      ? "bg-orange-50 text-orange-600 border-orange-200 hover:bg-orange-100"
                      : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                  )}
                >
                  <Heart size={14} className={cn(saved && "fill-orange-500 text-orange-500")} />
                  {saved ? "Đã lưu tin" : "Lưu tin"}
                </button>
              )}
            </CardContent>
          </Card>

          {/* Meta Info */}
          <Card>
            <CardHeader>
              <CardTitle>Chi tiết địa giới & ngày đăng</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-0">
                {[
                  ["Tỉnh/TP", provinceName],
                  ["Quận/Huyện", districtName],
                  ["Phường/Xã", wardName],
                  ["Quốc gia", countryName],
                  [
                    "Trạng thái",
                    item.approvalStatus === "approved"
                      ? "✓ Đã duyệt"
                      : "⏳ Chờ duyệt",
                  ],
                  [
                    "Ngày đăng",
                    item.createdAt ? new Date(item.createdAt).toLocaleDateString("vi-VN") : "—",
                  ],
                ].map(([k, v]) => (
                  <div
                    key={k}
                    className="flex justify-between items-start py-2 border-b last:border-0 gap-2"
                    style={{ borderColor: "var(--border-c)" }}
                  >
                    <span
                      className="text-xs shrink-0"
                      style={{ color: "var(--text-3)" }}
                    >
                      {k}
                    </span>
                    <span
                      className="text-xs font-medium text-right"
                      style={{ color: "var(--text-1)" }}
                    >
                      {v ?? "—"}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ══ OFFER MODAL ══ */}
      <OfferModal
        isOpen={offerOpen}
        onClose={() => { setOfferOpen(false); reset(); }}
        title="Gửi đề xuất hợp tác nhượng quyền"
        onSubmit={handleSubmit(onSubmitOffer)}
        isPending={isPending}
        submitText="Gửi đề xuất"
      >
        <NumberInput
          label="Giá nhượng quyền đề xuất (VNĐ)"
          placeholder="100,000,000"
          hint="Để trống nếu đồng ý theo ngân sách đề xuất"
          error={errors.proposedRent?.message}
          {...register('proposedRent')}
        />
        <Textarea
          label="Lời nhắn / Đề xuất hợp tác *"
          placeholder="Giới thiệu thương hiệu của bạn, khả năng đáp ứng nhu cầu nhượng quyền..."
          rows={4}
          error={errors.message?.message}
          {...register('message')}
        />
      </OfferModal>

      {/* ══ NDA MODAL ══ */}
      <NdaSignModal
        isOpen={ndaOpen}
        onClose={() => setNdaOpen(false)}
        entityType="FranchiseRequest"
        entityId={item.id}
        description="Bạn cần ký Thỏa thuận bảo mật thông tin (NDA) để trao đổi với người đăng."
        onSuccess={() => {
          setNdaOpen(false);
          setOfferOpen(true);
        }}
      />
    </div>
  );
}





